import moment from 'moment-timezone';
import { DATE_TIME_FORMATS } from '@fc-shared/utils/date-time-formats';
import { NgTagColor } from '@fc-shared/ui/tags/ng-tag.component';
import {
  ButtonColor,
  ButtonVariant,
} from '@fc-shared/ui/buttons/ng-button.directive';
import { AppIconsType } from '@fc-shared/ui/icon/icons';
import {
  BotAction,
  BotBlock,
  BotField,
  BotFieldsBlock,
  BotPanel,
  BotPanelCommand,
  BotRowsBlock,
  BotSeverity,
  BotTag,
  BotTagsBlock,
} from '@fc-chats/models/bot-widget';

export const PICKER_NEXT_PAGE_ACTION_ID = 'more';
export const PICKER_CANCEL_ACTION_ID = 'cancel';

export function hasPanelAction(
  panel: BotPanel | null | undefined,
  actionId: string,
): boolean {
  return panel?.actions?.some((action) => action.id === actionId) ?? false;
}

export function canLoadNextPickerPage(
  panel: BotPanel | null | undefined,
): boolean {
  return (
    panel?.type === 'bot.picker' &&
    hasPanelAction(panel, PICKER_NEXT_PAGE_ACTION_ID)
  );
}

// Generated bot help (backend chat/bots/help.py) sends its command list as a
// plain rows block — label = usage, value = description — so a rows block
// whose every label is a slash command is that list.
export function isCommandListBlock(block: BotRowsBlock): boolean {
  return (
    block.rows.length > 0 &&
    block.rows.every(
      (row) => row.label.startsWith('/') && !row.status && !row.url,
    )
  );
}

export function isFieldsCard(
  block: Pick<BotFieldsBlock, 'subtitle' | 'image' | 'icon' | 'status' | 'url'>,
): boolean {
  return !!(
    block.subtitle ||
    block.image ||
    block.icon ||
    block.status ||
    block.url
  );
}

function isRowListBlock(block: BotBlock | undefined): boolean {
  if (block?.block === 'rows')
    return !isCommandListBlock(block as BotRowsBlock);
  if (block?.block === 'fields') return !isFieldsCard(block as BotFieldsBlock);
  return false;
}

export function continuesRowList(blocks: BotBlock[], index: number): boolean {
  return (
    index > 0 &&
    isRowListBlock(blocks[index]) &&
    isRowListBlock(blocks[index - 1])
  );
}

export function rowsToCommands(block: BotRowsBlock): BotPanelCommand[] {
  return block.rows.map((row) => ({
    usage: row.label,
    description: row.value ?? '',
  }));
}

const SEVERITY_TAG_COLORS: Record<BotSeverity, NgTagColor> = {
  neutral: 'secondary',
  success: 'success',
  warning: 'warning',
  danger: 'error',
};

const SEVERITY_DOT_CLASSES: Record<BotSeverity, string> = {
  neutral: 'bg-secondary-300',
  success: 'bg-success-500',
  warning: 'bg-warning-500',
  danger: 'bg-error-500',
};

const SEVERITY_ACCENT_CLASSES: Record<BotSeverity, string> = {
  neutral: 'border-secondary-200',
  success: 'border-success-500',
  warning: 'border-warning-500',
  danger: 'border-error-500',
};

const BOT_ICON_NAMES: Record<string, AppIconsType> = {
  vehicle: 'icon-vehicles-outline',
  camera: 'icon-dvr-camera',
  tracker: 'icon-hardwired-tracker',
};

export function severityToTagColor(style: string | undefined): NgTagColor {
  return SEVERITY_TAG_COLORS[style as BotSeverity] ?? 'secondary';
}

export function severityToDotClass(style: string | undefined): string {
  return (
    SEVERITY_DOT_CLASSES[style as BotSeverity] ?? SEVERITY_DOT_CLASSES.neutral
  );
}

export function severityToAccentClass(style: string | undefined): string {
  return (
    SEVERITY_ACCENT_CLASSES[style as BotSeverity] ??
    SEVERITY_ACCENT_CLASSES.neutral
  );
}

export function actionStyleToButtonColor(action: BotAction): ButtonColor {
  return action.style === 'primary' ? 'primary' : 'secondary';
}

export function actionStyleToButtonVariant(action: BotAction): ButtonVariant {
  return action.style === 'primary' ? 'filled' : 'outline';
}

const PICKER_ITEM_KIND_ICONS: Record<string, string> = {
  dvr: 'camera',
  ng: 'camera',
  tracker: 'tracker',
  vehicle: 'vehicle',
};

export function pickerItemIcon(itemId: string): AppIconsType | null {
  const kind = itemId.split(':')[0];
  return botIconToAppIcon(PICKER_ITEM_KIND_ICONS[kind]);
}

export function botIconToAppIcon(
  icon: string | undefined,
): AppIconsType | null {
  return (icon && BOT_ICON_NAMES[icon]) || null;
}

export function formatDurationSeconds(totalSeconds: number): string {
  const duration = moment.duration(Math.max(0, Math.round(totalSeconds)), 's');
  const days = Math.floor(duration.asDays());
  const parts = [
    [days, 'd'],
    [duration.hours(), 'h'],
    [duration.minutes(), 'm'],
    [duration.seconds(), 's'],
  ] as const;
  const shown = parts.filter(([amount]) => amount > 0).slice(0, 2);
  return shown.length
    ? shown.map(([amount, suffix]) => `${amount}${suffix}`).join(' ')
    : '0s';
}

export function formatBotFieldValue(field: BotField): string {
  try {
    switch (field.type) {
      case 'number': {
        if (!('number' in field) || typeof field.number !== 'number') break;
        const formatted = field.number.toLocaleString();
        return field.unit ? `${formatted} ${field.unit}` : formatted;
      }
      case 'money': {
        if (!('amount' in field)) break;
        const amount = Number(field.amount);
        if (!Number.isFinite(amount)) break;
        return amount.toLocaleString(undefined, {
          style: 'currency',
          currency: field.currency,
        });
      }
      case 'duration':
        if (!('seconds' in field) || typeof field.seconds !== 'number') break;
        return formatDurationSeconds(field.seconds);
      case 'date': {
        if (!('date' in field)) break;
        const date = moment(field.date, moment.ISO_8601, true);
        if (!date.isValid()) break;
        return date.format(DATE_TIME_FORMATS.displayDate);
      }
      case 'datetime': {
        if (!('datetime' in field)) break;
        const datetime = moment(field.datetime, moment.ISO_8601, true);
        if (!datetime.isValid()) break;
        return datetime.format(DATE_TIME_FORMATS.displayTimestamp);
      }
    }
  } catch {
    return field.value;
  }
  return field.value;
}

// The panel contract lets a client draw a view's own status as the leading
// chip of a leading `tags` block, so those tags join the header's chip row
// instead of floating alone under it.
export function splitLeadingTags(blocks: BotBlock[] | undefined): {
  leadingTags: BotTag[];
  bodyBlocks: BotBlock[];
} {
  const [firstBlock, ...remainingBlocks] = blocks ?? [];
  if (firstBlock?.block === 'tags') {
    return {
      leadingTags: (firstBlock as BotTagsBlock).tags,
      bodyBlocks: remainingBlocks,
    };
  }
  return { leadingTags: [], bodyBlocks: blocks ?? [] };
}
